<template>
  <div class="content">
    <div class="user-management">
      <div class="row">
        <div class="col-md-12">
          <div style="margin-top: 20px">
            <div class="header-actions">
              <h5 class="title">Performance Target Score</h5>
              <div v-if="isAdmin" class="actions-right">
                <button class="add-employee" @click="openScoreModal()" style="width:170px">
                  <span>Add Target Score</span>
                </button>
              </div>
            </div>
            <div class="mt-4">
              <CustomTable
                :header="filteredColumns"
                :loading="loadingStates.table"
                :columns="filteredColumns"
                :data="scoreList"
              >
                <template v-slot:designation="{ item }">
                  <div class="criteria-name">{{ item.designation || '-' }}</div>
                </template>

                <template v-slot:grade="{ item }">
                  <span class="type-text">{{ item.gradeLabel || '-' }}</span>
                </template>

                <template v-slot:score="{ item }">
                  <span :class="['score-display', item.score < 0 ? 'text-danger' : 'text-success']">
                    {{ item.score !== undefined ? item.score : '-' }}
                  </span>
                </template>
                <template v-if="isAdmin" v-slot:action="{ item }">
                  <div class="action-buttons">
                    <button class="edit-btn btn-status" @click="editScore(item)">
                      <PencilIcon color="gray" :width="16" :height="16" />
                    </button>
                    <button class="delete-btn btn-status" @click="openDeleteModal(item)">
                      <i class="fa fa-trash" style="color: gray;"></i>
                    </button>
                  </div>
                </template>
              </CustomTable>
            </div>
          </div>
          <ReuseableModal
            :modelValue="isScoreModalOpen"
            @update:modelValue="isScoreModalOpen = $event"
            :title="selectedScore ? 'Edit Target Score' : 'Add Target Score'"
            :isLarge="true"
            submitButtonText="Save"
            @submit="saveScore()"
            :loading="loadingStates.save"
            :performance="true"
          >
            <form @submit.prevent="saveScore()">
              <div class="modal-body-container">
                <div class="form-scroll-container m-0 p-0">
                  <div class="form-group row" style="padding: 0px 12px">
                    <div class="col-md-12 mb-3">
                      <label>Designation <span style="color: red; font-size: 14px;">*</span></label>
                      <CustomDropdown
                        :options="designations"
                        :value="selectedDesignation"
                        placeholder="Select Designation"
                        :required="true"
                        @change="onDesignationChange"
                      />
                    </div>
                    <div class="col-md-12 mb-3">
                      <label>Grade <span style="color: red; font-size: 14px;">*</span></label>
                      <GradeDropdown
                        v-model="selectedGrade"
                        :designation="selectedDesignation"
                        :designationGrades="designationGrades"
                        placeholder="Select Grade"
                        :required="true"
                        :disabled="!selectedDesignation"
                        @change="onGradeChange"
                      />
                    </div>
                    <div class="col-md-12 mb-3">
                      <label>Target Score <span style="color: red; font-size: 14px;">*</span></label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="score"
                        min="1"
                        placeholder="e.g. 20 or 30"
                        style="height: 43px; border-radius: 4px"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </ReuseableModal>
          <ReuseableModal
            :modelValue="isDeleteModalOpen"
            @update:modelValue="isDeleteModalOpen = $event"
            title="Delete Target Score?"
            :isLarge="false"
            submitButtonText="Delete"
            @submit="deleteScore()"
            :loading="loadingStates.delete"
          >
            <div>
              <p class="m-0">Are you sure you want to permanently delete this target score?</p>
            </div>
          </ReuseableModal>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import PencilIcon from 'src/assets/icons/PencilIcon.js';
import ReuseableModal from "src/components/UIComponents/ReuseableModal.vue";
import CustomDropdown from '../../../UIComponents/CustomDropdown.vue';
import GradeDropdown from '../../../UIComponents/GradeDropdown.vue';   
import designationsData from '../../../data/UserProfileData/designations.json';
import designationGradesData from '../../../data/UserProfileData/designationGrades.json';
import firebase from "../../../../assets/config/firebase";
import { logAdminActivity } from "../../../../utils/adminLogs.js";

export default {
  components: {
    CustomTable,
    PencilIcon,
    ReuseableModal,
    CustomDropdown,
    GradeDropdown,          
  },
  props: {
    isAdmin: Boolean,
  },
  data() {
    return {
      tableColumns: [
        { label: "Designation", key: "designation", sortable: true, width: 280 },
        { label: "Grade", key: "grade", width: 340 },
        { label: "Target Score", key: "score", width: 140, align: "right" },
        { label: "Actions", key: "action", sticky: "right", width: 100 }
      ],
      scoreList: [],
      selectedScore: null,
      isScoreModalOpen: false,
      isDeleteModalOpen: false,
      selectedDesignation: "",
      selectedGrade: "",
      score: null,
      designations: designationsData.designations || [],
      designationGrades: designationGradesData.designationGrades || {},
      loadingStates: { table: false, save: false, delete: false },
    };
  },

  computed: {
    filteredColumns() {
      return this.isAdmin
        ? this.tableColumns
        : this.tableColumns.filter(c => c.key !== "action");
    }
  },

  mounted() {
    this.fetchScores();
  },

  methods: {
    onDesignationChange(value) {
      this.selectedDesignation = value;
      this.selectedGrade = "";   
    },

    onGradeChange(value) {
      this.selectedGrade = value;
    },

    openScoreModal() {
      this.resetForm();
      this.isScoreModalOpen = true;
    },

    editScore(item) {
      this.selectedScore = item;
      this.selectedDesignation = item.designation || "";
      this.selectedGrade = item.gradeValue || item.grade || "";
      this.score = item.score || 0;
      this.isScoreModalOpen = true;
    },

    resetForm() {
      this.selectedScore = null;
      this.selectedDesignation = "";
      this.selectedGrade = "";
      this.score = null;
    },

    hasChanges() {
      if (!this.selectedScore) return true;

      return (
        this.selectedDesignation !== (this.selectedScore.designation || "") ||
        this.selectedGrade !== (this.selectedScore.gradeValue || this.selectedScore.grade || "") ||
        Number(this.score) !== Number(this.selectedScore.score)
      );
    },

    async saveScore() {
      if (!this.selectedDesignation) {
        swal("Validation Error", "Designation is required", "error");
        return;
      }
      if (!this.selectedGrade) {
        swal("Validation Error", "Grade is required", "error");
        return;
      }
      if (this.score === null || this.score === "" || isNaN(this.score)) {
        swal("Validation Error", "Target score is required", "error");
        return;
      }
      if (this.score < 0) {
        swal("Validation Error", "Target score cannot be negative.", "error");
        return;
      }
      if (this.score === 0) {
        swal("Validation Error", "Target score cannot be zero", "error");
        return;
      }
      if (!this.hasChanges()) {
        swal("No Changes", "No changes were made to the target score.", "info");
        return;
      }

      const gradeList = this.designationGrades[this.selectedDesignation] || [];
      const selectedGradeObj = gradeList.find(g => String(g.value) === String(this.selectedGrade));

      const payload = {
        designation: this.selectedDesignation,
        grade: this.selectedGrade,
        gradeLabel: selectedGradeObj ? selectedGradeObj.label : this.selectedGrade,
        score: Number(this.score),
        updatedAt: new Date().toISOString()
      };

      try {
        this.loadingStates.save = true;

        if (this.selectedScore && this.selectedScore.uid) {
          const adminActivity = {
            adminId: this.$store.state.auth.user.uid,
            activityByUserName: this.$store.state.auth.user.username || 'Admin',
            action: "Updated",
            activityTitle: "Updated Target Score",
            activityDescription: `Performance target scores updated for ${selectedGradeObj ? selectedGradeObj.label : this.selectedGrade}: ${this.selectedScore.score} → ${payload.score}`,
            activityName: `Performance target scores updated for ${selectedGradeObj ? selectedGradeObj.label : this.selectedGrade}: ${this.selectedScore.score} → ${payload.score}`,
            type: "update_score_by_grade",
            timestamp: new Date().toLocaleString('en-PK'),
            uid: this.selectedScore.uid
          };
          await logAdminActivity(adminActivity);
          await firebase.updatePerformanceScoreByGrade(this.selectedScore.uid, payload);
          swal("Success!", "Target score updated successfully", "success");
        } else {
          const adminActivity = {
            adminId: this.$store.state.auth.user.uid,
            activityByUserName: this.$store.state.auth.user.username || 'Admin',
            action: "Added",
            activityTitle: "Added Target Score",
            activityDescription: `Performance target scores added for ${selectedGradeObj ? selectedGradeObj.label : this.selectedGrade}: ${payload.score}`,
            activityName: `Performance target scores added for ${selectedGradeObj ? selectedGradeObj.label : this.selectedGrade}: ${payload.score}`,
            type: "add_score_by_grade",
            timestamp: new Date().toLocaleString('en-PK')
          };
          await logAdminActivity(adminActivity);
          await firebase.addPerformanceScoreByGrade(payload);
          swal("Success!", "Target score added successfully", "success");
        }

        await this.fetchScores();
        this.isScoreModalOpen = false;
      } catch (err) {
        console.error(err);
        swal("Error", err.message || "Failed to save target score", "error");
      } finally {
        this.loadingStates.save = false;
      }
    },

    openDeleteModal(item) {
      this.selectedScore = item;
      this.isDeleteModalOpen = true;
    },

    async deleteScore() {
      try {
        this.loadingStates.delete = true;
        await firebase.deletePerformanceScoreByGrade(this.selectedScore.uid);
        const adminActivity = {
          adminId: this.$store.state.auth.user.uid,
          activityByUserName: this.$store.state.auth.user.username || 'Admin',
          action: "Deleted",
          activityTitle: "Deleted Target Score",
          activityDescription: `Performance target scores deleted for ${this.selectedScore.gradeLabel}: ${this.selectedScore.score}`,
          activityName: `Performance target scores deleted for ${this.selectedScore.gradeLabel}: ${this.selectedScore.score}`,
          type: "delete_score_by_grade",
          timestamp: new Date().toLocaleString('en-PK'),
          uid: this.selectedScore.uid
        };
        await logAdminActivity(adminActivity);
        await this.fetchScores();
        swal("Deleted", "Target score removed successfully", "success");
        this.isDeleteModalOpen = false;
      } catch (err) {
        swal("Error", "Failed to delete target score", "error");
      } finally {
        this.loadingStates.delete = false;
      }
    },

    async fetchScores() {
      this.loadingStates.table = true;
      try {
        const list = await firebase.getAllPerformanceScoresByGrade();
        this.scoreList = list || [];
      } catch (err) {
        console.error(err);
        swal("Error", "Could not load target scores", "error");
      } finally {
        this.loadingStates.table = false;
      }
    }
  }
};
</script>

<style scoped>
.user-management .form-group input:focus {
  border-color: #ddd !important; 
  box-shadow: none; 
}
.form-control:focus {
  box-shadow: none !important;
}
.score-display {
  font-weight: 500;
}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/PerformanceCriteria/PerformanceByGrade.vue